import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAncho } from '../../hooks/useAncho';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { ArrowLeft, CalendarDays, AlertCircle, Share2 } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import {
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  AlertDescription,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../ui';
import EntityLogo from '../ui/EntityLogo';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  getJornadasTorneo,
  getJornadasDetalle,
  getRolJornada,
  getTablaPosiciones,
  getBracket,
  getTablaGrupos,
  getTorneoCabecera,
  getSedesTorneo
} from '../../lib/services/torneoJuegos';
import { getInscripcionesByTorneo, getTorneoCategoriasConvocadas } from '../../lib/services/inscripciones';
import { tienePorGrupo, agruparPosicionesPorGrupo } from '../../lib/torneoClubView';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn, fixMojibake } from '../../lib/utils';
import RolJornadaGrid from './RolJornadaGrid';
import Convocatoria from './Convocatoria';
import { supabase } from '../../lib/supabase';
import RolJornadaCards from './RolJornadaCards';
import BracketView from './BracketView';
import CompartirRolModal from './CompartirRolModal';
import { armarRolesPorSede } from '../../rol/mapeoRol';
import { enlaceSeguro } from '../../lib/enlaceSeguro';
import { divisionesDelClub, etiquetaDivision, idDivision } from '../../lib/division';

const formatFecha = (fecha) => {
  if (!fecha) return null;
  try {
    const parsed = parseLocalDate(fecha);
    return parsed ? format(parsed, "d 'de' MMM yyyy", { locale: es }) : null;
  } catch {
    return null;
  }
};

// Tabla de posiciones (columnas JJ/JG/JE/JP/PF/PC/DIF/PTS), reutilizada plana o por grupo.
// Replica renderTablaPos de Torneos (TorneoJuegos.js) + resalta las filas del club.
const TablaPosiciones = ({ rows, misInscripcionIds }) => (
  <div className="overflow-x-auto rounded-lg border">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="h-8 w-6 py-1.5 pr-0 text-xs">#</TableHead>
          <TableHead className="h-8 py-1.5">Equipo</TableHead>
          <TableHead className="h-8 py-1.5 text-right">JJ</TableHead>
          <TableHead className="hidden h-8 py-1.5 text-right md:table-cell">JG</TableHead>
          <TableHead className="hidden h-8 py-1.5 text-right md:table-cell">JE</TableHead>
          <TableHead className="hidden h-8 py-1.5 text-right md:table-cell">JP</TableHead>
          <TableHead className="hidden h-8 py-1.5 text-right md:table-cell">PF</TableHead>
          <TableHead className="hidden h-8 py-1.5 text-right md:table-cell">PC</TableHead>
          <TableHead className="h-8 py-1.5 text-right">DIF</TableHead>
          <TableHead className="h-8 py-1.5 text-right">PTS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((p, i) => {
          const destacado = misInscripcionIds.has(p.inscripcion_id);
          return (
            <TableRow key={p.inscripcion_id} className={cn(destacado && 'bg-primary/5')}>
              <TableCell className="py-1.5 pr-0 text-xs text-muted-foreground tabular-nums">{i + 1}</TableCell>
              <TableCell className="py-1.5">
                <div className="flex min-w-0 items-center gap-2">
                  <EntityLogo src={p.logo} name={p.club} className="h-6 w-6 shrink-0" />
                  <div className="min-w-0 leading-tight">
                    <p className="truncate text-sm font-medium">{p.club}</p>
                    {p.grupo && <p className="truncate text-[11px] text-muted-foreground">{p.grupo}</p>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-1.5 text-right text-sm tabular-nums">{p.jj}</TableCell>
              <TableCell className="hidden py-1.5 text-right text-sm tabular-nums md:table-cell">{p.jg}</TableCell>
              <TableCell className="hidden py-1.5 text-right text-sm tabular-nums md:table-cell">{p.je}</TableCell>
              <TableCell className="hidden py-1.5 text-right text-sm tabular-nums md:table-cell">{p.jp}</TableCell>
              <TableCell className="hidden py-1.5 text-right text-sm tabular-nums md:table-cell">{p.pf}</TableCell>
              <TableCell className="hidden py-1.5 text-right text-sm tabular-nums md:table-cell">{p.pc}</TableCell>
              <TableCell className="py-1.5 text-right text-sm tabular-nums">{p.dif > 0 ? `+${p.dif}` : p.dif}</TableCell>
              <TableCell className="py-1.5 text-right text-sm font-semibold tabular-nums">{p.pts}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

// Chip de navegación (tira con scroll horizontal), mismo estilo que los chips de Torneos.
const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={cn('shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-sm transition',
      active ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-accent')}>
    {children}
  </button>
);

const ParticipacionDetail = () => {
  const { id: torneoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { org } = useOrganization();

  const [torneoData, setTorneoData] = useState(null);
  // Nombre del torneo y liga: para el encabezado y el flyer. location.state es solo un
  // atajo cuando llegamos desde la lista; si viene vacio, lo tomamos de la cabecera (RPC
  // gateado; el club participante no puede leer `ligas` por RLS con un SELECT normal).
  const torneoNombre = torneoData?.nombre || location.state?.nombre || null;
  const ligaNombre = torneoData?.liga_nombre || null;
  const flyerBg = torneoData?.flyer_bg_url || null;

  useEffect(() => {
    if (!torneoId) return;
    let cancel = false;
    getTorneoCabecera(torneoId).then(({ data }) => {
      if (!cancel) setTorneoData(data || null);
    });
    return () => { cancel = true; };
  }, [torneoId]);

  // Sedes del torneo (nombre, mapa, canchas): una vez por torneo. Si la RPC falla todo sigue
  // como antes, con los nombres de sede que traen los partidos y sin mapas.
  const [sedes, setSedes] = useState([]);
  useEffect(() => {
    if (!torneoId) return;
    let cancel = false;
    getSedesTorneo(torneoId).then(({ data, error }) => {
      if (!cancel) setSedes(error ? [] : (data || []));
    }).catch(() => { if (!cancel) setSedes([]); });
    return () => { cancel = true; };
  }, [torneoId]);

  const [jornadas, setJornadas] = useState([]);
  const [jornadasInfo, setJornadasInfo] = useState([]);
  const [jornadasLoading, setJornadasLoading] = useState(true);
  const [jornadasError, setJornadasError] = useState(null);
  const [selectedJornada, setSelectedJornada] = useState(null);

  const [rolRows, setRolRows] = useState([]);
  const [rolLoading, setRolLoading] = useState(false);
  const [rolError, setRolError] = useState(null);

  const [posDivisionId, setPosDivisionId] = useState('');
  const [posicionesRows, setPosicionesRows] = useState([]);
  const [posicionesLoading, setPosicionesLoading] = useState(false);
  const [posicionesError, setPosicionesError] = useState(null);

  const [misCategorias, setMisCategorias] = useState([]);       // [{id (division), categoriaId, nombre}] donde el club tiene equipos
  const [misInscripcionIds, setMisInscripcionIds] = useState(new Set());
  const [misCategoriasLoaded, setMisCategoriasLoaded] = useState(false);
  // Filas de get_torneo_categorias_convocadas: una por division (categoria + genero).
  const [divisionesTorneo, setDivisionesTorneo] = useState([]);

  const [bracketRows, setBracketRows] = useState([]);
  const [bracketLoading, setBracketLoading] = useState(false);

  const [compartirOpen, setCompartirOpen] = useState(false);

  // La convocatoria: las reglas contra las que se valida el roster. Antes solo se veia
  // ANTES de aceptar la invitacion; despues desaparecia, justo cuando el club la
  // necesita para armar sus equipos.
  const [convocatoria, setConvocatoria] = useState(null);
  const [convLoading, setConvLoading] = useState(true);

  // Mis inscripciones en este torneo, con su grupo: la puerta al roster.
  const [misEquipos, setMisEquipos] = useState([]);

  useEffect(() => {
    if (!torneoId) return;
    let cancel = false;
    setConvLoading(true);
    supabase.rpc('get_convocatoria', { p_torneo_id: torneoId }).then(({ data, error }) => {
      if (cancel) return;
      setConvocatoria(error ? null : data);
      setConvLoading(false);
    });
    return () => { cancel = true; };
  }, [torneoId]);

  useEffect(() => {
    if (!torneoId) return;
    let cancel = false;
    getInscripcionesByTorneo(torneoId).then(({ data }) => {
      if (cancel) return;
      setMisEquipos((data || []).filter((i) => !org?.id || i.club_org_id === org.id));
    });
    return () => { cancel = true; };
  }, [torneoId, org?.id]);

  const loadMisCategorias = useCallback(async () => {
    if (!torneoId) return;
    const [insRes, catRes] = await Promise.all([
      getInscripcionesByTorneo(torneoId),
      getTorneoCategoriasConvocadas(torneoId),
    ]);
    const propias = (insRes.data || []).filter((i) => !org?.id || i.club_org_id === org.id);
    // Los nombres vienen de las categorias CONVOCADAS del torneo (RPC legible por el club):
    // el join directo a `categorias` lo bloquea la RLS cuando la categoria es de la
    // organizadora. Cada fila convocada es una DIVISION (categoria + genero, 20261005):
    // quedan solo las divisiones donde el club tiene equipos.
    setDivisionesTorneo(catRes.data || []);
    // fixMojibake al NOMBRE antes de componer la etiqueta: sobre "Niños U14 · Femenil" ya
    // armado dañaria el punto medio.
    setMisCategorias(divisionesDelClub(
      (catRes.data || []).map((c) => ({ ...c, nombre: fixMojibake(c.nombre) })),
      propias
    ));
    setMisInscripcionIds(new Set(propias.map((i) => i.id)));
    setMisCategoriasLoaded(true);
  }, [torneoId, org?.id]);

  useEffect(() => { loadMisCategorias(); }, [loadMisCategorias]);

  const misCategoriaIds = useMemo(() => new Set(misCategorias.map((c) => c.id)), [misCategorias]);

  const loadJornadas = useCallback(async () => {
    if (!torneoId) return;
    setJornadasLoading(true);
    setJornadasError(null);
    const { data, error } = await getJornadasTorneo(torneoId);
    if (error) {
      setJornadasError(error);
      setJornadas([]);
    } else {
      setJornadas(data);
      setSelectedJornada((prev) => (prev && data.includes(prev) ? prev : (data[0] ?? null)));
    }
    const { data: det } = await getJornadasDetalle(torneoId);
    setJornadasInfo(det || []);
    setJornadasLoading(false);
  }, [torneoId]);

  useEffect(() => { loadJornadas(); }, [loadJornadas]);

  const loadRol = useCallback(async () => {
    if (!torneoId || selectedJornada == null) {
      setRolRows([]);
      return;
    }
    setRolLoading(true);
    setRolError(null);
    const { data, error } = await getRolJornada(torneoId, selectedJornada);
    if (error) {
      setRolError(error);
      setRolRows([]);
    } else {
      setRolRows(data);
    }
    setRolLoading(false);
  }, [torneoId, selectedJornada]);

  useEffect(() => { loadRol(); }, [loadRol]);

  // Acotar el rol a las categorias donde el club tiene equipos (lista blanca). Antes de
  // que la lista blanca cargue, no ocultar todo (fallback a rolRows sin filtrar); una vez
  // cargada, filtrar estricto aunque este vacia (club con licencia pero sin inscripciones
  // aun no debe ver las categorias de otros clubes).
  const rolRowsScoped = useMemo(
    () => (misCategoriasLoaded ? rolRows.filter((r) => misCategoriaIds.has(idDivision(r))) : rolRows),
    [rolRows, misCategoriaIds, misCategoriasLoaded]
  );

  const categoriasEnRol = useMemo(() => {
    const map = new Map();
    rolRowsScoped.forEach((r) => {
      const id = idDivision(r);
      if (id && !map.has(id)) {
        map.set(id, etiquetaDivision({ nombre: fixMojibake(r.categoria_nombre), rama: r.rama }));
      }
    });
    return Array.from(map, ([id, nombre]) => ({ id, nombre }));
  }, [rolRowsScoped]);

  useEffect(() => {
    if (misCategorias.length === 0) {
      setPosDivisionId('');
      return;
    }
    // Default: preferir una categoria donde el club de verdad juega (aparece en el
    // rol), para no caer en una inscripcion vacia sin juegos.
    const conJuegos = misCategorias.find((c) => categoriasEnRol.some((rc) => rc.id === c.id));
    setPosDivisionId((prev) => (misCategorias.some((c) => c.id === prev) ? prev : (conJuegos?.id || misCategorias[0].id)));
  }, [misCategorias, categoriasEnRol]);

  const loadPosiciones = useCallback(async () => {
    if (!torneoId || !posDivisionId) {
      setPosicionesRows([]);
      return;
    }
    setPosicionesLoading(true);
    setPosicionesError(null);
    try {
      const grupos = await getTablaGrupos(torneoId, null, posDivisionId);
      if (!grupos.error && tienePorGrupo(grupos.data)) {
        setPosicionesRows(grupos.data);      // trae grupo_torneo
      } else {
        const plano = await getTablaPosiciones(torneoId, null, posDivisionId);
        if (plano.error) {
          setPosicionesError(plano.error);
          setPosicionesRows([]);
        } else {
          setPosicionesRows(plano.data || []);
        }
      }
    } catch (e) {
      setPosicionesError(e);
      setPosicionesRows([]);
    } finally {
      setPosicionesLoading(false);
    }
  }, [torneoId, posDivisionId]);

  useEffect(() => { loadPosiciones(); }, [loadPosiciones]);

  const loadBracket = useCallback(async () => {
    if (!torneoId || !posDivisionId) {
      setBracketRows([]);
      return;
    }
    setBracketLoading(true);
    try {
      const { data } = await getBracket(torneoId, null, posDivisionId);
      setBracketRows(data || []);
    } catch {
      setBracketRows([]);
    } finally {
      setBracketLoading(false);
    }
  }, [torneoId, posDivisionId]);

  useEffect(() => { loadBracket(); }, [loadBracket]);

  // Jornada "programada" (tiene campo/hora) -> rejilla; si no -> tarjetas.
  // El Rol muestra la jornada COMPLETA (todos los juegos, con su campo/hora), no solo
  // las categorias del club: es el calendario del dia. Los juegos del club se resaltan
  // por nombre. (Posiciones y Eliminatoria si quedan acotadas a las categorias del club.)
  const programada = useMemo(() => rolRows.some((r) => r.campo || r.hora), [rolRows]);
  // En el celular la rejilla (varias columnas por sede) no cabe: tarjetas, ya agrupadas por sede.
  const ancho = useAncho();

  const posicionesAgrupadas = useMemo(
    () => (tienePorGrupo(posicionesRows) ? agruparPosicionesPorGrupo(posicionesRows) : null),
    [posicionesRows]
  );

  const refreshAll = useCallback(async () => {
    await loadMisCategorias();
    await loadJornadas();
    await loadRol();
    await loadPosiciones();
    await loadBracket();
  }, [loadMisCategorias, loadJornadas, loadRol, loadPosiciones, loadBracket]);

  const titulo = jornadasInfo.find((x) => x.numero === selectedJornada)?.nombre || `Jornada ${selectedJornada}`;

  // La division de cada equipo (categoria + genero), desde la convocatoria.
  const etiquetaDeEquipo = (i) => {
    const d = i.convocatoria_id && divisionesTorneo.find((c) => c.convocatoria_id === i.convocatoria_id);
    return d
      ? etiquetaDivision({ ...d, nombre: fixMojibake(d.nombre) })
      : fixMojibake(i.categorias?.nombre || i.categoria || 'Sin categoría');
  };

  // Una imagen por sede cuando la jornada se juega en varias; con una sola, la de siempre.
  const rolesPorSede = useMemo(() => armarRolesPorSede({
    torneo: { nombre: torneoNombre, sede: torneoData?.sede, campos: torneoData?.campos || [], ligas: ligaNombre ? { nombre: ligaNombre, logo_url: torneoData?.liga_logo || null } : null },
    jornadaInfo: jornadasInfo.find((x) => x.numero === selectedJornada) || null,
    jornada: selectedJornada,
    filas: rolRows,
    organizadora: torneoData?.organizadora_nombre || null,
    sedes,
  }), [torneoNombre, torneoData, ligaNombre, jornadasInfo, selectedJornada, rolRows, sedes]);

  return (
    <FeaturedLayout
      title={torneoNombre ? fixMojibake(torneoNombre) : 'Torneo externo'}
      subtitle={
        convocatoria?.organizador
          ? `Organiza ${fixMojibake(convocatoria.organizador)}. Tu club participa: aquí ves las reglas, tus equipos y el rol.`
          : 'Tu club participa en este torneo. Aquí ves las reglas, tus equipos y el rol.'
      }
      refreshAction={refreshAll}
    >
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/torneos-myklubb')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Mis torneos
        </Button>

        <Tabs defaultValue="convocatoria">
          <TabsList>
            <TabsTrigger value="convocatoria">Convocatoria</TabsTrigger>
            <TabsTrigger value="equipos">Mis equipos</TabsTrigger>
            <TabsTrigger value="rol">Rol</TabsTrigger>
            <TabsTrigger value="posiciones">Posiciones</TabsTrigger>
            <TabsTrigger value="eliminatoria">Eliminatoria</TabsTrigger>
          </TabsList>

          {/* Las reglas del torneo: categorias convocadas con su rango congelado,
              fecha limite de roster, tope de jugadores y regla de dorsal. */}
          <TabsContent value="convocatoria" className="space-y-4">
            {convLoading ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Cargando convocatoria...</p>
            ) : convocatoria ? (
              <Card><CardContent className="p-4"><Convocatoria convocatoria={convocatoria} /></CardContent></Card>
            ) : (
              <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
                La organizadora todavía no publicó la convocatoria de este torneo.
              </CardContent></Card>
            )}
          </TabsContent>

          {/* Los grupos que este club inscribio aqui. El roster de cada uno se abre en
              su propia pantalla: es lo unico que el club SI edita de este torneo. */}
          <TabsContent value="equipos" className="space-y-4">
            {misEquipos.length === 0 ? (
              <Card><CardContent className="py-10 text-center space-y-3">
                <p className="text-sm text-muted-foreground">Todavía no inscribes ningún grupo en este torneo.</p>
                <Button size="sm" onClick={() => navigate('/torneos-myklubb')}>Inscribir un grupo</Button>
              </CardContent></Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {misEquipos.map((i) => (
                  <Card key={i.id}>
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {fixMojibake(i.grupos?.nombre_del_grupo || 'Grupo')}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {etiquetaDeEquipo(i)}
                          {i.estatus === 'solicitada' && ' · esperando respuesta de la organizadora'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => navigate(`/torneos/${torneoId}/inscripciones/${i.id}`)}
                      >
                        Ver roster
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rol" className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-56">
                <Select value={selectedJornada != null ? String(selectedJornada) : ''}
                  onValueChange={(v) => setSelectedJornada(Number(v))} disabled={jornadas.length === 0}>
                  <SelectTrigger><SelectValue placeholder="Elige una jornada" /></SelectTrigger>
                  <SelectContent>
                    {jornadas.map((j) => (
                      <SelectItem key={j} value={String(j)}>
                        {jornadasInfo.find((x) => x.numero === j)?.nombre || `Jornada ${j}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => setCompartirOpen(true)}
                disabled={!rolRows.some((r) => r.hora)}
                title={!rolRows.some((r) => r.hora) ? 'Programa el calendario primero' : undefined}
              >
                <Share2 className="h-4 w-4 mr-1" /> Compartir imagen
              </Button>
            </div>

            {sedes.length > 1 && (
              <p className="text-sm text-muted-foreground">
                Sedes:{' '}
                {sedes.map((x, i) => {
                  const mapa = enlaceSeguro(x.maps_url);
                  return (
                    <React.Fragment key={x.sede_id || x.nombre}>
                      {i > 0 && ' · '}
                      {mapa ? (
                        <a href={mapa} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                          {fixMojibake(x.nombre)}
                        </a>
                      ) : fixMojibake(x.nombre)}
                    </React.Fragment>
                  );
                })}
              </p>
            )}

            {(() => {
              const ji = jornadasInfo.find((j) => j.numero === selectedJornada);
              if (!ji) return null;
              const v = [
                ji.fecha ? formatFecha(ji.fecha) : null,
                ji.hora_inicio ? `${ji.hora_inicio.slice(0, 5)}${ji.hora_fin ? '–' + ji.hora_fin.slice(0, 5) : ''}` : null,
                ji.sede ? fixMojibake(ji.sede) : null,
              ].filter(Boolean).join(' · ');
              return v ? <p className="text-sm text-muted-foreground">{v}</p> : null;
            })()}

            {jornadasLoading || rolLoading ? (
              <p className="text-muted-foreground">Cargando...</p>
            ) : jornadasError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No se pudo cargar el rol</AlertTitle>
                <AlertDescription>{jornadasError.message || 'Ocurrió un error inesperado.'}</AlertDescription>
              </Alert>
            ) : rolError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{rolError.message || 'Ocurrió un error inesperado.'}</AlertDescription>
              </Alert>
            ) : jornadas.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CalendarDays className="mx-auto mb-3 h-10 w-10 text-muted-foreground opacity-30" />
                  <p className="text-sm text-muted-foreground">Aún no hay juegos programados para este torneo.</p>
                </CardContent>
              </Card>
            ) : programada && ancho ? (
              <RolJornadaGrid juegos={rolRows} highlightName={org?.name} sedes={sedes} />
            ) : (
              <RolJornadaCards juegos={rolRows} highlightName={org?.name} sedes={sedes} />
            )}
          </TabsContent>

          <TabsContent value="posiciones" className="space-y-4">
            {misCategorias.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {misCategorias.map((c) => (
                  <Chip key={c.id} active={posDivisionId === c.id} onClick={() => setPosDivisionId(c.id)}>
                    {c.nombre}
                  </Chip>
                ))}
              </div>
            )}
            {misCategoriasLoaded && misCategorias.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Tu club aún no tiene categorías inscritas en este torneo.</CardContent></Card>
            ) : posicionesLoading ? (
              <p className="text-muted-foreground">Cargando...</p>
            ) : posicionesError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{posicionesError.message || 'Ocurrió un error inesperado.'}</AlertDescription>
              </Alert>
            ) : posicionesRows.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aún no hay juegos finalizados en esta división.</CardContent></Card>
            ) : posicionesAgrupadas ? (
              posicionesAgrupadas.map((g, i) => (
                <div key={g.grupo || i} className="space-y-1.5">
                  <h3 className="text-sm font-semibold text-muted-foreground">{g.grupo ? `Grupo ${fixMojibake(g.grupo)}` : 'Grupo'}</h3>
                  <TablaPosiciones rows={g.filas} misInscripcionIds={misInscripcionIds} />
                </div>
              ))
            ) : (
              <TablaPosiciones rows={posicionesRows} misInscripcionIds={misInscripcionIds} />
            )}
          </TabsContent>

          <TabsContent value="eliminatoria" className="space-y-4">
            {misCategorias.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {misCategorias.map((c) => (
                  <Chip key={c.id} active={posDivisionId === c.id} onClick={() => setPosDivisionId(c.id)}>
                    {c.nombre}
                  </Chip>
                ))}
              </div>
            )}
            {misCategoriasLoaded && misCategorias.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Tu club aún no tiene categorías inscritas en este torneo.</CardContent></Card>
            ) : bracketLoading ? (
              <p className="text-muted-foreground">Cargando...</p>
            ) : bracketRows.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aún no hay eliminatoria en esta división.</CardContent></Card>
            ) : (
              <BracketView juegos={bracketRows} highlightIds={misInscripcionIds} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <CompartirRolModal
        open={compartirOpen}
        onOpenChange={setCompartirOpen}
        titulo={titulo}
        fondoUrl={flyerBg}
        roles={rolesPorSede}
      />
    </FeaturedLayout>
  );
};

export default ParticipacionDetail;
